import { useEffect, useState } from "react";
import type { JSX } from "react";
import { api, ApiError } from "./api";
import {
  BankIcon,
  ChartIcon,
  ChatIcon,
  DeedIcon,
  ExchangeIcon,
  KeyIcon,
  MapIcon,
  PickaxeIcon,
  TrophyIcon,
  TruckIcon,
  UserIcon,
} from "./icons";
import { Inspector } from "./Inspector";
import { PropertyPanel } from "./PropertyCard";
import { WorldView } from "./WorldView";
import { AdminWindow } from "./admin";
import { BusinessWindow, OffersWindow } from "./business";
import { CommunityWindow } from "./chat";
import { BankWindow, ExchangeWindow, LeaderboardWindow, LeasesWindow, PortfolioWindow } from "./panels";
import { fmtMoney } from "./format";
import { startRealtime, stopRealtime } from "./realtime";
import { generationProgress, useApp, type WindowId } from "./store";
import { Ticker } from "./Ticker";

export function App(): JSX.Element {
  const session = useApp((s) => s.session);
  const [booting, setBooting] = useState(true);

  useEffect(() => {
    api.session().then(
      async (s) => {
        useApp.getState().setSession(s);
        try {
          useApp.getState().setDashboard(await api.dashboard());
        } catch {
          // Not fatal; the HUD fills in on the next refresh.
        }
        setBooting(false);
      },
      () => setBooting(false),
    );
  }, []);

  if (booting) {
    return (
      <div className="gate">
        <p className="muted">Connecting to the world…</p>
      </div>
    );
  }
  if (!session) return <AuthScreen />;
  if (!session.homeChunkId) return <SettleScreen />;
  return <Shell />;
}

/* ------------------------------------------------------------------ */

function AuthScreen(): JSX.Element {
  const [mode, setMode] = useState<"login" | "register">("register");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      if (mode === "register") await api.register(email, password, displayName);
      else await api.login(email, password);
      const s = await api.session();
      useApp.getState().setSession(s);
      useApp.getState().setDashboard(await api.dashboard());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "could not sign in");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="gate">
      <form
        className="box"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div className="wordmark">Parcelbound</div>
        <p className="tagline">A world of property, running all night</p>
        <p className="sub">
          A permanent, shared property world. Start anywhere on the globe, buy land, build, and let
          it out.
        </p>
        {error && <div className="notice err">{error}</div>}
        {mode === "register" && (
          <div className="field">
            <label htmlFor="name">Display name</label>
            <input
              id="name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              autoComplete="nickname"
              required
              minLength={2}
              maxLength={24}
            />
          </div>
        )}
        <div className="field">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </div>
        <div className="field">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={mode === "register" ? "new-password" : "current-password"}
            required
            minLength={mode === "register" ? 12 : 1}
          />
          {mode === "register" && (
            <p className="small faint" style={{ margin: "6px 0 0" }}>
              At least 12 characters.
            </p>
          )}
        </div>
        <button className="primary" style={{ width: "100%" }} disabled={busy} type="submit">
          {mode === "register" ? "Create account" : "Sign in"}
        </button>
        <div className="switch">
          {mode === "register" ? "Already playing?" : "New here?"}
          <button
            type="button"
            onClick={() => {
              setMode(mode === "register" ? "login" : "register");
              setError(null);
            }}
          >
            {mode === "register" ? "Sign in" : "Create an account"}
          </button>
        </div>
      </form>
    </div>
  );
}

function SettleScreen(): JSX.Element {
  const [lon, setLon] = useState("-74.0000");
  const [lat, setLat] = useState("40.7358");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const settle = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await api.settle(Number(lon), Number(lat));
      const s = await api.session();
      useApp.getState().setSession(s);
      useApp.getState().setDashboard(await api.dashboard());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "could not settle there");
    } finally {
      setBusy(false);
    }
  };

  const presets: Array<[string, string, string]> = [
    ["Lower Manhattan", "-74.0000", "40.7358"],
    ["Paris", "2.3522", "48.8566"],
    ["Copenhagen", "12.5683", "55.6761"],
    ["Rural Jutland", "8.6212", "56.3626"],
  ];

  return (
    <div className="gate">
      <div className="box wide">
        <div className="wordmark">Choose a home</div>
        <p className="sub">
          Pick any coordinates on Earth. We generate that area, grant you 50,000 starter credits and
          a food-cart permit, and point you at the cheapest unit nearby. Generated property is
          permanent.
        </p>
        {error && <div className="notice err">{error}</div>}
        <div className="field">
          <label>Suggested starts</label>
          <div className="chips">
            {presets.map(([label, plon, plat]) => (
              <button
                key={label}
                aria-pressed={lon === plon && lat === plat}
                onClick={() => {
                  setLon(plon);
                  setLat(plat);
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="lon">Longitude</label>
            <input id="lon" value={lon} onChange={(e) => setLon(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="lat">Latitude</label>
            <input id="lat" value={lat} onChange={(e) => setLat(e.target.value)} />
          </div>
        </div>
        <button className="primary" style={{ width: "100%" }} disabled={busy} onClick={() => void settle()}>
          {busy ? "Generating your neighbourhood…" : "Settle here"}
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

const DOCK: Array<{
  id: WindowId;
  label: string;
  Icon: (p: { className?: string }) => JSX.Element;
}> = [
  { id: "portfolio", label: "Portfolio", Icon: DeedIcon },
  { id: "business", label: "Businesses", Icon: PickaxeIcon },
  { id: "leases", label: "Leases", Icon: KeyIcon },
  { id: "offers", label: "Offers", Icon: TruckIcon },
  { id: "exchange", label: "Exchange", Icon: ExchangeIcon },
  { id: "bank", label: "Bank", Icon: BankIcon },
  { id: "inspector", label: "Inspector", Icon: MapIcon },
];

function DockButton({
  id,
  label,
  Icon,
}: {
  id: WindowId;
  label: string;
  Icon: (p: { className?: string }) => JSX.Element;
}): JSX.Element {
  const openWindows = useApp((s) => s.openWindows);
  const toggleWindow = useApp((s) => s.toggleWindow);
  const selectedId = useApp((s) => s.selectedId);
  return (
    <button aria-pressed={openWindows.includes(id)} aria-label={label} onClick={() => toggleWindow(id)}>
      <Icon />
      {id === "inspector" && selectedId && <span className="dot" aria-hidden />}
      <span className="tip">{label}</span>
    </button>
  );
}

function Shell(): JSX.Element {
  const dashboard = useApp((s) => s.dashboard);
  const session = useApp((s) => s.session);
  const toast = useApp((s) => s.toast);
  const chunkStates = useApp((s) => s.chunkStates);
  const openWindows = useApp((s) => s.openWindows);
  const toggleWindow = useApp((s) => s.toggleWindow);
  const selectedId = useApp((s) => s.selectedId);

  // The simulation clock advances a game day per real hour. The realtime
  // socket refreshes immediately on events; this poll is the fallback.
  useEffect(() => {
    const tick = setInterval(() => {
      api.dashboard().then(
        (d) => useApp.getState().setDashboard(d),
        () => undefined,
      );
    }, 20_000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    if (!session) return;
    startRealtime(session.id);
    return () => stopRealtime();
  }, [session?.id]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => useApp.getState().clearToast(), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  const progress = generationProgress(chunkStates);
  const income = dashboard ? Number(dashboard.incomePerGameDay) : 0;
  const incomeTone = income > 0 ? "up" : income < 0 ? "down" : "flat";

  return (
    <div className="app">
      <WorldView />

      <header className="hud">
        <span className="wordmark">Parcelbound</span>

        <div className="stat optional">
          <span className="k">Game day</span>
          <span className="v">
            {dashboard ? dashboard.gameDay : "—"}
            <span className="rate">{dashboard?.gameTimeOfDay ?? ""}</span>
          </span>
        </div>

        <div className="stat">
          <span className="k">Money</span>
          <span className="v money">
            {dashboard ? fmtMoney(dashboard.wallet) : "—"}
            <span className={`rate ${incomeTone}`}>
              {dashboard
                ? `${income >= 0 ? "+" : "−"}$${Math.abs(income).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}/day`
                : ""}
            </span>
          </span>
        </div>

        <div className="stat">
          <span className="k">Net worth</span>
          <span className="v">{dashboard ? fmtMoney(dashboard.netWorth) : "—"}</span>
        </div>

        <div className="stat optional">
          <span className="k">Holdings</span>
          <span className="v">
            {dashboard ? `${dashboard.parcelsOwned}L · ${dashboard.buildingsOwned}B` : "—"}
            <span className="rate">{dashboard ? `${dashboard.unitsLeasedOut} let` : ""}</span>
          </span>
        </div>

        <span className="spacer" />

        {session?.isStaff && (
          <button className="pill" aria-pressed={openWindows.includes("admin")} onClick={() => toggleWindow("admin")}>
            Staff
          </button>
        )}
        <button className="pill" onClick={() => toggleWindow("leaderboard")}>
          <TrophyIcon />
          Leaderboards
        </button>
        <button
          className="pill"
          aria-pressed={openWindows.includes("community")}
          onClick={() => toggleWindow("community")}
        >
          <ChatIcon />
          Community
        </button>
        <button
          className="pill"
          onClick={() => {
            void api.logout().finally(() => useApp.getState().reset());
          }}
          title={`Signed in as ${session?.displayName ?? ""} — sign out`}
        >
          <UserIcon />
          {session?.displayName ?? "Account"}
        </button>
      </header>

      {toast && <div className={`notice ${toast.kind} toast`}>{toast.text}</div>}

      {(progress.busy || progress.failed) && (
      <div className="gen-indicator">
        {progress.busy && <span className="spinner" aria-hidden />}
        <span>{progress.label}</span>
        {progress.failed && (
          <button
            className="retry"
            onClick={() => {
              const failed = Object.entries(chunkStates)
                .filter(([, s]) => s === "failed")
                .map(([id]) => id);
              if (failed.length > 0) void api.ensureChunks(failed);
            }}
          >
            Retry
          </button>
        )}
      </div>
      )}

      <div className="attribution">
        Map data ©{" "}
        <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">
          OpenStreetMap
        </a>{" "}
        contributors · gameplay values fictional · not real property data
      </div>

      <nav className="dock" aria-label="Panels">
        {DOCK.slice(0, 4).map((d) => (
          <DockButton key={d.id} {...d} />
        ))}
        {/* The player, front and centre, the way the reference frames its
            dock. Clicking recentres the camera on the in-world avatar. */}
        <button
          type="button"
          className="dock-avatar"
          aria-label="Go to my character"
          onClick={() => useApp.getState().bumpGoToPlayer()}
        >
          <span className="head" />
          <span className="torso" />
          <span className="tip">Go to my character</span>
        </button>
        {DOCK.slice(4).map((d) => (
          <DockButton key={d.id} {...d} />
        ))}
        <button aria-pressed={openWindows.includes("leaderboard")} aria-label="Rankings"
          onClick={() => toggleWindow("leaderboard")}>
          <ChartIcon />
          <span className="tip">Rankings</span>
        </button>
      </nav>

      <PropertyPanel />

      {openWindows.includes("inspector") && <Inspector />}
      {openWindows.includes("portfolio") && <PortfolioWindow />}
      {openWindows.includes("business") && <BusinessWindow />}
      {openWindows.includes("offers") && <OffersWindow />}
      {openWindows.includes("exchange") && <ExchangeWindow />}
      {openWindows.includes("bank") && <BankWindow />}
      {openWindows.includes("leases") && <LeasesWindow />}
      {openWindows.includes("leaderboard") && <LeaderboardWindow />}
      {openWindows.includes("community") && <CommunityWindow />}
      {openWindows.includes("admin") && session?.isStaff && <AdminWindow />}

      <Ticker />
    </div>
  );
}
